import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getHairSalonApiBaseUrl } from '../../utils/hairSalonApiDeps';
import {
  HairSalonBrandLogo,
  useHairSalonOrgLogo,
  SalonHeaderActions,
  navigateHairFlowHome,
  persistProductCart,
  syncAnalysisProductCartToCheckin,
  syncAnalysisRecommendedProductsReady,
  modernConfirm,
  readStoredFranchiseBrandName,
  ReportPdfQrPanel,
  confirmAnalysisServicesAndStart,
  treatmentCartToPendingServices,
} from '../../utils/hairSalonFlowDeps';
import { useHairSession } from '../../context/HairSessionContext';
import {
  fetchInsightTreatmentRecommendations,
  fetchInsightProductRecommendations,
  normalizeRecommendGender,
} from '../../utils/hairTreatmentRecommendationsApi';
import {
  resolveHairGuestUserDataForAnalysis,
  resolveHairSalonVisitSessionId,
} from '../../utils/hairSalonFlowDeps';
import { resolveHairReportCustomer } from '../../utils/reportCustomer';
import {
  createInsightReportPdfDownloadUrl,
  getInsightDashboard,
} from '../../utils/insightApi';
import { hairAppPath, resolveInsightSessionId, setHairInsightSessionId } from '../../config/hairApp';
import {
  HairInsightReportCharts,
  HairInsightReportFindings,
  HairInsightReportFooterBands,
  HairInsightReportHeader,
  HairInsightReportMicroscope,
  HairInsightReportOverview,
  HairInsightReportScalpDetail,
} from './HairInsightReportSections';
import HairInsightReportTreatmentsSection from './HairInsightReportTreatmentsSection';
import HairInsightReportProductsSection from './HairInsightReportProductsSection';
import '../../../../../enterprise/salon/pages/SkinRoutinePage.css';
import '../../../../../naturals/salon/pages/SkinRoutinePage.css';
import './HairInsightReport.css';
import './HairInsightReportSkinLayout.css';
import './HairInsight.css';

export default function HairInsightDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const microRef = useRef(null);

  const seedDashboard = location.state?.dashboard || null;
  const [dashboard, setDashboard] = useState(seedDashboard);
  const [loading, setLoading] = useState(!seedDashboard);
  const [loadError, setLoadError] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [activeMicroView, setActiveMicroView] = useState(null);
  const [recommendedTreatments, setRecommendedTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState('');
  const [treatmentCartItems, setTreatmentCartItems] = useState({});
  const [recommendedProducts, setRecommendedProducts] = useState({ morning: [], evening: [] });
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productCartItems, setProductCartItems] = useState({});
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [serviceConfirmNotice, setServiceConfirmNotice] = useState('');
  const [productConfirmNotice, setProductConfirmNotice] = useState('');

  const { token, sessionLandingPath } = useHairSession();
  const { orgLogoUrl, showLogo, onLogoError } = useHairSalonOrgLogo();
  const orgName = useMemo(() => readStoredFranchiseBrandName(), [orgLogoUrl]);

  const insightSessionId = dashboard?.sessionId || resolveInsightSessionId(location, seedDashboard);
  const salonSessionId = resolveHairSalonVisitSessionId();
  const sessionId = salonSessionId || insightSessionId;
  const guestGender = useMemo(() => {
    const c = resolveHairReportCustomer(dashboard);
    const profile = resolveHairGuestUserDataForAnalysis(location.state || {});
    return (
      normalizeRecommendGender(c.gender) ||
      normalizeRecommendGender(profile.gender) ||
      ''
    );
  }, [dashboard, location.state]);

  const treatmentsForDisplay = useMemo(
    () => (Array.isArray(recommendedTreatments) ? recommendedTreatments : []),
    [recommendedTreatments],
  );

  useEffect(() => {
    if (seedDashboard?.sessionId) {
      setHairInsightSessionId(seedDashboard.sessionId);
    }
  }, [seedDashboard]);

  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;
  const treatmentsFetchedKeyRef = useRef('');
  const productsFetchedKeyRef = useRef('');
  const checkinHairReportSyncedRef = useRef('');

  const loadTreatments = useCallback((force = false) => {
    const dash = dashboardRef.current;
    if (!dash?.sessionId) return;
    const fetchKey = `${dash.sessionId}:${salonSessionId || ''}`;
    if (!force && treatmentsFetchedKeyRef.current === fetchKey) return;
    treatmentsFetchedKeyRef.current = fetchKey;

    const cached = Array.isArray(dash.recommendedTreatments) ? dash.recommendedTreatments : [];
    if (cached.length) {
      setRecommendedTreatments(cached);
    }
    setTreatmentsLoading(true);
    setTreatmentsError('');
    void fetchInsightTreatmentRecommendations(dash, {
      token,
      limit: 8,
      gender: guestGender,
      hairInsightSessionId: dash.sessionId,
      sessionId: salonSessionId || undefined,
    })
      .then((data) => {
        const list = Array.isArray(data?.recommended_treatments) ? data.recommended_treatments : [];
        setRecommendedTreatments(list);
      })
      .catch((err) => {
        if (force) treatmentsFetchedKeyRef.current = '';
        setRecommendedTreatments([]);
        const msg = String(err?.message || '');
        setTreatmentsError(
          /429|too many/i.test(msg)
            ? 'Service recommendations are busy — wait a moment and tap Retry.'
            : msg || 'Could not load recommended services',
        );
      })
      .finally(() => setTreatmentsLoading(false));
  }, [token, guestGender, salonSessionId]);

  const loadProducts = useCallback((force = false) => {
    const dash = dashboardRef.current;
    if (!dash?.sessionId) return;
    const fetchKey = `${dash.sessionId}:${salonSessionId || ''}`;
    if (!force && productsFetchedKeyRef.current === fetchKey) return;
    productsFetchedKeyRef.current = fetchKey;

    setProductsLoading(true);
    setProductsError('');
    void fetchInsightProductRecommendations(dash, {
      token,
      limit: 8,
      gender: guestGender,
      sessionId: salonSessionId || undefined,
    })
      .then((data) => {
        const morning = Array.isArray(data?.recommended_products?.morning) ? data.recommended_products.morning : [];
        const evening = Array.isArray(data?.recommended_products?.evening) ? data.recommended_products.evening : [];
        setRecommendedProducts({ morning, evening });
        if (morning.length || evening.length) {
          void syncAnalysisRecommendedProductsReady('hair_analysis', {
            salonReference: salonSessionId || '',
            hairInsightSessionId: dash?.sessionId || '',
            productCount: morning.length + evening.length,
          });
        }
      })
      .catch((err) => {
        if (force) productsFetchedKeyRef.current = '';
        setRecommendedProducts({ morning: [], evening: [] });
        setProductsError(err?.message || 'Could not load recommended products');
      })
      .finally(() => setProductsLoading(false));
  }, [token, guestGender, salonSessionId]);

  useEffect(() => {
    if (dashboard) return undefined;

    const insightSessionId = resolveInsightSessionId(location, null);
    if (!insightSessionId) {
      setLoading(false);
      return undefined;
    }

    setHairInsightSessionId(insightSessionId);
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);
      const delays = [0, 400, 900, 1600];
      for (let i = 0; i < delays.length; i += 1) {
        if (cancelled) return;
        if (delays[i] > 0) {
          await new Promise((r) => window.setTimeout(r, delays[i]));
        }
        if (cancelled) return;
        try {
          const res = await getInsightDashboard(insightSessionId);
          const payload = res?.dashboard || res?.analysis;
          if (payload) {
            setDashboard(payload);
            return;
          }
        } catch (e) {
          const retryable =
            i < delays.length - 1 &&
            (/not ready|not found|404/i.test(String(e.message)) ||
              /temporarily unavailable/i.test(String(e.message)));
          if (!retryable && !cancelled) {
            setLoadError(e.message || 'Could not load the Hair Insight report.');
            return;
          }
        }
      }
      if (!cancelled) {
        setLoadError('Report is not ready yet. Please wait a moment and try again.');
      }
    })().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [dashboard, location.search, location.state?.insightSessionId]);

  useEffect(() => {
    if (!dashboard?.sessionId) return undefined;
    loadTreatments(false);
    loadProducts(false);
    return undefined;
  }, [dashboard?.sessionId, salonSessionId, loadTreatments, loadProducts]);

  useEffect(() => {
    const hairId = String(dashboard?.sessionId || '').trim();
    if (!hairId.startsWith('HA-')) return;
    const syncKey = `${hairId}:${salonSessionId || ''}`;
    if (checkinHairReportSyncedRef.current === syncKey) return;
    checkinHairReportSyncedRef.current = syncKey;
    void syncAnalysisRecommendedProductsReady('hair_analysis', {
      salonReference: salonSessionId || '',
      hairInsightSessionId: hairId,
      productCount: 0,
    });
  }, [dashboard?.sessionId, salonSessionId]);

  const treatmentCartTotal = Object.values(treatmentCartItems || {}).reduce(
    (sum, row) => sum + (Number(row?.quantity) || 0),
    0,
  );
  const productCartTotal = Object.values(productCartItems || {}).reduce(
    (sum, row) => sum + (Number(row?.quantity) || 0),
    0,
  );

  useEffect(() => {
    if (!salonSessionId) return undefined;
    persistProductCart(getHairSalonApiBaseUrl(), salonSessionId, token, productCartItems);
    void syncAnalysisProductCartToCheckin(productCartItems, 'hair_analysis');
    return undefined;
  }, [productCartItems, salonSessionId, token]);

  const handleConfirmServices = async () => {
    if (confirmBusy || treatmentCartTotal <= 0) return;
    const ok = await modernConfirm(
      `Confirm ${treatmentCartTotal} selected service(s) for this visit? They will be started for the stylist desk.`,
      { title: 'Confirm services', confirmLabel: 'Confirm', cancelLabel: 'Cancel' },
    );
    if (!ok) return;
    setConfirmBusy(true);
    setServiceConfirmNotice('');
    try {
      const lines = treatmentCartToPendingServices(treatmentCartItems, 'hair_analysis');
      await confirmAnalysisServicesAndStart(lines, 'hair_analysis');
      setServiceConfirmNotice('Services confirmed and started for this visit.');
    } catch (err) {
      setServiceConfirmNotice(err?.message || 'Could not confirm services. Please try again.');
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleConfirmProducts = async () => {
    if (confirmBusy || productCartTotal <= 0) return;
    const ok = await modernConfirm(
      `Confirm ${productCartTotal} selected product(s) for the front desk?`,
      { title: 'Confirm products', confirmLabel: 'Confirm', cancelLabel: 'Cancel' },
    );
    if (!ok) return;
    setConfirmBusy(true);
    setProductConfirmNotice('');
    try {
      if (salonSessionId) {
        await persistProductCart(getHairSalonApiBaseUrl(), salonSessionId, token, productCartItems);
      }
      await syncAnalysisProductCartToCheckin(productCartItems, 'hair_analysis');
      setProductConfirmNotice('Products confirmed for the front desk.');
    } catch (err) {
      setProductConfirmNotice(err?.message || 'Could not confirm products.');
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleHome = useCallback(() => {
    navigateHairFlowHome(navigate, sessionLandingPath);
  }, [navigate, sessionLandingPath]);

  const createPdfDownloadUrl = useCallback(async () => {
    if (!dashboard) throw new Error('Report not ready');
    return createInsightReportPdfDownloadUrl(
      {
        sessionId: insightSessionId,
        orgName,
        orgLogoUrl: showLogo ? orgLogoUrl : '',
        recommendedTreatments,
      },
      insightSessionId,
    );
  }, [insightSessionId, orgName, orgLogoUrl, showLogo, recommendedTreatments]);

  const onFocusEvidence = (ev) => {
    if (ev?.microscopeView || ev?.view) {
      setActiveMicroView(ev.microscopeView || ev.view);
    }
    microRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="hair-insight-page page-bg hi-scroll-page hi-analyzing-page">
        <div className="hi-analyzing-stage">
          <div className="hi-analyzing-card">
            <div className="hi-spinner" aria-hidden />
            <p className="hi-analyzing-step">Loading your Hair Insight report…</p>
            <p className="hi-muted">This may take a moment after analysis completes.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="hi-report page-bg">
        <div className="hi-report__shell">
          <div className="hi-report__card hi-report__empty">
            <h1>{loadError ? 'Report unavailable' : 'Insight unavailable'}</h1>
            <p>
              {loadError ||
                'Start a new Hair Insight session to view results.'}
            </p>
            <button
              type="button"
              className="hi-report__btn primary"
              onClick={() => navigate(hairAppPath('/insight/capture'))}
            >
              Start capture
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="skin-routine-page-shell page-bg hi-report-page-shell hi-report page-bg"
      id="hi-insight-report-shell"
      ref={scrollRef}
    >
      <div className="skin-routine-page-nav skin-report-nav hi-report-nav" data-pdf-exclude="true">
        <div className="skin-report-nav-left">
          <div className="skin-routine-page-brand skin-report-nav-brand">
            <HairSalonBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              className="skin-routine-page-brand-logo"
              imgClassName="skin-routine-page-brand-logo-img"
              fallbackClassName="skin-routine-page-brand-fallback"
              alt={orgName || 'Organization'}
            />
            {orgName ? (
              <div className={`skin-report-brand-copy${showLogo ? ' skin-report-brand-copy--with-logo' : ''}`}>
                <span className="skin-report-brand-org">{orgName}</span>
                <span className="skin-report-brand-title">AI Hair Analysis Report</span>
              </div>
            ) : (
              <div className="skin-report-brand-copy">
                <span className="skin-report-brand-title">AI Hair Analysis Report</span>
              </div>
            )}
          </div>
        </div>
        <div className="skin-routine-page-nav-right skin-report-nav-right">
          <SalonHeaderActions
            homeButtonClassName="skin-routine-page-home-button"
            onHome={handleHome}
          />
        </div>
      </div>

      <main className="skin-routine-page-main skin-report-page-main" id="hi-insight-report-main">
        <div className="skin-routine-page-container">
          <div className="skin-routine-page-layout skin-report-root hi-report__shell">
            <HairInsightReportHeader
              dashboard={dashboard}
              orgLogoUrl={orgLogoUrl}
              showLogo={showLogo}
              onLogoError={onLogoError}
              orgName={orgName}
            />

            <ReportPdfQrPanel
              enabled={Boolean(dashboard)}
              createDownloadUrl={createPdfDownloadUrl}
              ctaLabel="Download report"
              title="Scan to download PDF"
              subtitle="Scan with your phone to save your report as a PDF."
              className="hi-report-pdf-qr skin-report-pdf-qr"
            />

            {dashboard.partialAnalysis ? (
              <div className="hi-report__warn">Overview-only report — scalp close-ups were skipped.</div>
            ) : null}

            <HairInsightReportOverview dashboard={dashboard} />
            <HairInsightReportCharts dashboard={dashboard} />

            <div ref={microRef}>
              <HairInsightReportMicroscope
                dashboard={dashboard}
                selectedAnnotation={selectedAnnotation}
                onSelectAnnotation={setSelectedAnnotation}
                activeView={activeMicroView}
                onActiveView={setActiveMicroView}
              />
            </div>

            <HairInsightReportScalpDetail dashboard={dashboard} activeView={activeMicroView} />
            <HairInsightReportFindings dashboard={dashboard} onFocus={onFocusEvidence} />

            <HairInsightReportTreatmentsSection
              treatments={treatmentsForDisplay}
              loading={treatmentsLoading}
              error={treatmentsError}
              sessionId={salonSessionId}
              token={token}
              treatmentCartItems={treatmentCartItems}
              onTreatmentCartChange={setTreatmentCartItems}
              onRetry={() => loadTreatments(true)}
            />

            <HairInsightReportProductsSection
              products={recommendedProducts}
              loading={productsLoading}
              error={productsError}
              visible={treatmentCartTotal > 0}
              productCartItems={productCartItems}
              onProductCartChange={setProductCartItems}
              onRetry={() => loadProducts(true)}
              confirmBusy={confirmBusy}
              confirmNotice={productConfirmNotice}
              onConfirmProducts={handleConfirmProducts}
            />

            <HairInsightReportFooterBands dashboard={dashboard} />

            {treatmentCartTotal > 0 ? (
              <div className="skin-routine-next-treatments-inline hi-report-report-ctas" data-pdf-exclude="true">
                <button
                  type="button"
                  className="skin-action-button skin-routine-continue-treatments-button"
                  disabled={confirmBusy}
                  onClick={handleConfirmServices}
                >
                  {confirmBusy ? 'CONFIRMING…' : `CONFIRM SERVICES (${treatmentCartTotal})`}
                </button>
                {serviceConfirmNotice ? (
                  <p className="skin-routine-summary-text" role="status">{serviceConfirmNotice}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
